/**
 * The sounds in a line, for Sneakers' mouth.
 *
 * The course carries phonemes per **word**, because the scheduler uses them for
 * the pronunciation gap map. His lines are sentences, so this is the join: split
 * the line, and hand each word's sounds to the viseme track
 * ([D-081](../../../docs/DECISIONS.md#d-081)).
 *
 * **It does not ask the server.** A mouth is not worth a request, and a line
 * whose words are all unknown still moves — `speak` turns an unknown word into a
 * short open-and-close rather than a pause, because a mouth that stops while a
 * voice carries on is worse than one that is roughly right.
 */

import { ipaPhonemes, lookupForm, pieces } from '@capytalk/core';

/**
 * What the app knows about how a word sounds.
 *
 * Filled by the hint lookup as a child plays — every word the course teaches
 * arrives with its phonemes for another reason, so this is free. Empty at the
 * start of a session, which means his first line moves roughly and his later
 * ones move properly.
 */
const known = new Map<string, readonly string[]>();

/**
 * Remember how a word sounds, from the IPA the hint card already fetched.
 *
 * IPA rather than the phoneme array because IPA is what is on the wire: the
 * hint lookup sends it so a grown-up can read a pronunciation, and parsing it
 * here means a moving mouth costs no extra request and no schema change.
 */
export function learnt(word: string, ipa: string): void {
  const sounds = ipaPhonemes(ipa);
  if (sounds.length > 0) known.set(lookupForm(word), sounds);
}

/**
 * One entry per word in the line, in order; undefined where it is unknown.
 *
 * `pieces` rather than `wordsIn`: `wordsIn` answers *"which words are on this
 * screen"* and so returns each one once, which is right for a lookup request and
 * wrong here. A mouth follows the voice, and the voice says `hello` twice in
 * `Hello there, hello!` — de-duplicating would shorten the track and slide every
 * later sound out of step with the sound being made.
 */
export const phonemesOf = (line: string): readonly (readonly string[] | undefined)[] =>
  pieces(line).flatMap((piece) =>
    piece.lookup === null ? [] : [known.get(piece.lookup)],
  );

/** Test seam: forget everything. */
export const forgetPhonemes = (): void => known.clear();
